import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { alpha, Chip, Grid, Paper, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChipSection } from '@/types/sections/ChipSection';
import { ChipItem } from '@/types/ChipItem';
import Icon, { getIconName } from '@/components/Icon/Icon';

type ChipsComponentProps = BoxProps & {
  section: ChipSection;
};

export default function ChipsComponent({ sx, section, ...other }: ChipsComponentProps) {
  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container
        component={MotionViewport}
        sx={{
          alignItems: 'center',
          textAlign: 'center',
          my: { xs: 2, md: 10 },
          py: { xs: 2, lg: 0 },
        }}>
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography
              variant="h3"
              sx={{ mx: 'auto', maxWidth: 640, my: 3, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div variants={varFade('inUp')}>
            <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <m.div variants={varFade('inUp')}>
          <Grid
            container
            spacing={3}
            sx={{
              width: 1,
              pt: 4,
              justifyContent: 'center',
              alignItems: 'stretch',
            }}>
            {section?.chips?.map((item, index) => (
              <Grid key={`chips-grid-item-${index}`} size={{ xs: 12, sm: 6, md: 4 }}>
                {section.alignment === 'center' ? (
                  <GridChipItem
                    key={index}
                    index={index}
                    item={{
                      id: `chip-item-${index}`,
                      ...item,
                    }}
                  />
                ) : (
                  <GridChipLeftItem
                    key={index}
                    index={index}
                    item={{
                      id: `chip-item-${index}`,
                      ...item,
                    }}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </m.div>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type ChipsItemProps = {
  index: number;
  item: { id: string } & ChipItem;
};
function GridChipItem({ item, index }: ChipsItemProps) {
  const theme = useTheme();
  return (
    <Paper
      className="grid-item-paper"
      elevation={6}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start', // Ensures content starts at the top
        alignItems: 'center',
        textAlign: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderRadius: 0,
      }}>
      {/* Icon Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '80px', // Fixed height for the icon container
          mb: 2, // Adds spacing between the icon and the text
        }}>
        {item.icon ? (
          <Box
            sx={{
              color: alpha(theme.palette.primary.main, 0.24),
              width: '90px', // Fixed width for the icon
              height: '80px', // Fixed height for the icon
            }}>
            <Icon
              name={getIconName(item.icon, 'unknown')}
              style={{ width: '100%', height: 'auto' }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: '90px',
              height: '80px',
            }}
          />
        )}
      </Box>

      {/* Text Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          flexGrow: 1, // Ensures consistent space allocation
        }}>
        {item.uppertitle && (
          <Typography
            variant="body1"
            sx={{
              color: 'primary.main',
            }}>
            {item.uppertitle}
          </Typography>
        )}

        {item.title && (
          <Typography
            variant="h5"
            sx={{
              color: 'primary.main',
              //minHeight: '48px', // Ensures consistent title alignment
              display: 'flex',
              alignItems: 'center', // Vertically centers the title content
              justifyContent: 'center', // Horizontally centers the title content
            }}>
            {item.title}
          </Typography>
        )}

        {item.subtitle && (
          <Typography
            variant="body1"
            sx={{
              color: 'primary.main',
              minHeight: '32px', // Ensures consistent subtitle alignment
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            {item.subtitle}
          </Typography>
        )}
      </Box>

      {/* Tags Section */}
      {item?.tags && item?.tags?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            width: '100%',
            mt: 3,
          }}>
          {item.tags.map((tag, idx) => (
            <Chip
              key={`tags-${idx}`}
              label={tag.tag || ''}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: alpha(theme.palette.primary.main, 0.5),
                borderRadius: '50px',
                py: 0.5,
                px: 1,
                fontSize: '0.65rem',
                fontWeight: 500,
                height: 'auto',
                lineHeight: 1.2,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08), // Keep the same background color
                  cursor: 'default', // Prevent pointer cursor
                },
              }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
}

function GridChipLeftItem({ item, index }: ChipsItemProps) {
  const theme = useTheme();
  return (
    <Paper
      className="grid-item-paper"
      elevation={6}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start', // Ensures content starts at the top
        alignItems: 'flex-start', // Aligns content to the left
        textAlign: 'left',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderRadius: 0,
      }}>
      {/* Icon Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start', // Aligns the icon to the left
          alignItems: 'center',
          width: '100%',
          height: '80px', // Fixed height for the icon container
          mb: 2, // Adds spacing between the icon and the text
        }}>
        {item.icon ? (
          <Box
            sx={{
              color: alpha(theme.palette.primary.main, 0.24),
              width: '90px', // Fixed width for the icon
              height: '80px', // Fixed height for the icon
            }}>
            <Icon
              name={getIconName(item.icon, 'unknown')}
              style={{ width: '100%', height: 'auto' }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: '90px',
              height: '80px',
            }}
          />
        )}
      </Box>

      {/* Text Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          width: '100%',
          flexGrow: 1, // Ensures consistent space allocation
        }}>
        {item.uppertitle && (
          <Typography
            variant="body1"
            sx={{
              color: 'primary.main',
            }}>
            {item.uppertitle}
          </Typography>
        )}

        {item.title && (
          <Typography
            variant="h5"
            sx={{
              color: 'primary.main',
              minHeight: '48px', // Ensures consistent title alignment
              display: 'flex',
              alignItems: 'center', // Vertically centers the title content
            }}>
            {item.title}
          </Typography>
        )}

        {item.subtitle && (
          <Typography
            variant="body1"
            sx={{
              color: 'primary.main',
              minHeight: '32px', // Ensures consistent subtitle alignment
              display: 'flex',
              alignItems: 'center',
            }}>
            {item.subtitle}
          </Typography>
        )}
      </Box>

      {/* Tags Section */}
      {item?.tags && item?.tags?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap', // Allows wrapping if there are too many tags
            gap: 1, // Adds spacing between tags
            width: '100%',
            mt: 3,
          }}>
          {item.tags.map((tag, idx) => (
            <Chip
              key={`tags-${idx}`}
              label={tag.tag || ''}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: alpha(theme.palette.primary.main, 0.5),
                borderRadius: '50px',
                py: 0.5,
                px: 1,
                fontSize: '0.65rem',
                fontWeight: 500,
                height: 'auto',
                lineHeight: 1.2,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08), // Keep the same background color
                  cursor: 'default', // Prevent pointer cursor
                },
              }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
}
